import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { AlertCircle, Loader2, ArrowLeft } from 'lucide-react';
// Mismo diseño que registro y login (8-oct-2026): mitad azul compartida (AuthMarca). Lógica sin cambios.
import { PanelMarca, FUENTE, INPUT, LABEL } from '../components/AuthMarca';
const BOTON = 'h-12 w-full rounded-full bg-[#0057B8] text-[15px] font-bold text-white shadow-lg shadow-[#0057B8]/25 transition hover:bg-[#004494] focus:outline-none focus:ring-4 focus:ring-[#0057B8]/30 disabled:opacity-60 inline-flex items-center justify-center';
import { baseAuthApi } from '../../../api/baseAuthApi';

// Página de fijar contraseña contra lealify-base. Sirve DOS flujos según `mode`:
//  - 'reset'  (/restablecer?token=): el dueño/operador olvidó su contraseña.
//  - 'invite' (/activar?token=):     operador invitado fija su contraseña inicial y activa la cuenta.
// Es el destino de los enlaces que manda authEmails.js. Reemplaza al handler de Firebase
// /auth/action. Valida el token, fija la contraseña e inicia sesión (cookie) → redirige al dashboard.
const RestablecerPassword: React.FC<{ mode?: 'reset' | 'invite' }> = ({ mode = 'reset' }) => {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const isInvite = mode === 'invite';
  const titleTxt = isInvite ? 'Activa tu cuenta' : 'Restablecer contraseña';
  const ctaTxt = isInvite ? 'Crear mi contraseña' : 'Restablecer contraseña';

  const [phase, setPhase] = useState<'checking' | 'valid' | 'invalid'>('checking');
  const [email, setEmail] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!token) { setPhase('invalid'); return; }
      try {
        const info = await (isInvite ? baseAuthApi.peekInviteToken(token) : baseAuthApi.peekResetToken(token));
        if (cancelled) return;
        if (info.valid) { setEmail(info.email || null); setPhase('valid'); }
        else setPhase('invalid');
      } catch {
        if (!cancelled) setPhase('invalid');
      }
    })();
    return () => { cancelled = true; };
  }, [token, isInvite]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (password.length < 8) { setError('La contraseña debe tener al menos 8 caracteres.'); return; }
    if (password !== confirm) { setError('Las contraseñas no coinciden.'); return; }
    setSubmitting(true);
    try {
      await (isInvite ? baseAuthApi.activateAccount(token, password) : baseAuthApi.resetPassword(token, password));
      // El backend ya dejó la sesión (cookie). Recarga completa para que AuthProvider la restaure.
      window.location.assign('/dashboard');
    } catch (err: any) {
      setError(err?.message || 'No se pudo restablecer la contraseña. El enlace pudo haber vencido.');
      setSubmitting(false);
    }
  };

  // flex-shrink-0: #root mide 100vh y es flex (misma trampa que registro y login en celular).
  return (
    <div className="force-light min-h-screen flex-shrink-0 flex flex-col md:flex-row bg-white" style={FUENTE}>
      <PanelMarca
        titulo="Tus clientes vuelven más seguido."
        lineaMovil=""
        beneficios={['Tu kiosco y tu QR, listos en el mostrador', 'Quién vuelve y quién no, de un vistazo', 'Tus clientes, sus puntos y sus premios']}
      />

      <main className="flex flex-1 items-center justify-center px-5 py-8 md:px-12 md:py-12">
        <div className="w-full max-w-[400px]">
          <h2 style={FUENTE} className="text-center text-[28px] md:text-[32px] font-extrabold tracking-tight text-[#0E1E3A]">{titleTxt}</h2>

          {phase === 'checking' && (
            <div className="flex flex-col items-center py-8 text-slate-600">
              <Loader2 className="h-6 w-6 animate-spin mb-2" />
              <p className="text-sm">Validando tu enlace…</p>
            </div>
          )}

          {phase === 'invalid' && (
            <div className="pt-6">
              <div className="mb-5 flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 p-4">
                <AlertCircle className="h-5 w-5 flex-shrink-0 text-red-500" />
                <p className="text-sm text-red-700">{isInvite ? 'Esta invitación es inválida o ya venció. Pide que te reenvíen el acceso a tu negocio.' : 'Este enlace de recuperación es inválido o ya venció. Solicita uno nuevo desde la pantalla de inicio de sesión.'}</p>
              </div>
              <Link to="/login" className="flex items-center justify-center text-sm font-semibold text-[#0057B8] hover:underline">
                <ArrowLeft className="h-4 w-4 mr-1" /> Volver a iniciar sesión
              </Link>
            </div>
          )}

          {phase === 'valid' && (
            <>
              <p className="mt-2 mb-7 text-center text-sm text-slate-600">
                {isInvite
                  ? (email ? <>Crea tu contraseña para activar tu acceso a <strong className="text-[#0E1E3A]">{email}</strong>.</> : 'Crea tu contraseña para activar tu acceso.')
                  : (email ? <>Crea una nueva contraseña para <strong className="text-[#0E1E3A]">{email}</strong>.</> : 'Crea una nueva contraseña para tu cuenta.')}
              </p>
              {error && (
                <div className="mb-5 flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                  <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />{error}
                </div>
              )}
              <form className="space-y-4" onSubmit={handleSubmit}>
                <div>
                  <label htmlFor="new-password" className={LABEL}>Nueva contraseña</label>
                  <input id="new-password" type="password" autoComplete="new-password" required value={password}
                    onChange={(e) => { setPassword(e.target.value); setError(null); }}
                    className={INPUT} placeholder="Mínimo 8 caracteres" />
                </div>
                <div>
                  <label htmlFor="confirm-password" className={LABEL}>Confirmar contraseña</label>
                  <input id="confirm-password" type="password" autoComplete="new-password" required value={confirm}
                    onChange={(e) => { setConfirm(e.target.value); setError(null); }}
                    className={INPUT} placeholder="Repite la contraseña" />
                </div>
                <div className="pt-2">
                  <button type="submit" disabled={submitting} className={BOTON}>
                    {submitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Guardando…</> : ctaTxt}
                  </button>
                </div>
              </form>
              <div className="mt-5 text-center">
                <Link to="/login" className="inline-flex items-center text-sm font-semibold text-slate-600 hover:text-[#0E1E3A]">
                  <ArrowLeft className="h-4 w-4 mr-1" /> Volver a iniciar sesión
                </Link>
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default RestablecerPassword;
